import get from 'lodash.get';

import env from '#shared/config/env.js';
import getProviderPaymentMethods from '#shared/util/getProviderPaymentMethods.js';
import getSlug from '#shared/util/getSlug.js';
import isObject from '#shared/util/isObject.js';
import setDefaultPaymentMethod from '#shared/util/setDefaultPaymentMethod.js';
import deduplicatePaymentOptions from '#shared/util/deduplicatePaymentOptions.js';

import { providerAccountSchema } from './account.js';
import { customerSchema } from './customer.js';

/**
 * Map of NLOK payment method names to the checkout ones
 */
const providerToCheckoutTypeMap = {
	BANKXFER: 'wireTransfer',
	CC: 'creditCard',
	CREDIT_CARD: 'creditCard',
	DD: 'directDebit',
	IDEAL: 'iDeal',
	PP: 'payPalBilling',
	PAYPAL: 'payPalBilling',
	SOFO: 'sofort',
	BOLETO: 'boleto'
};

/**
 * Map of checkout payment method names to the NLOK ones
 */
const checkoutToProviderTypeMap = {
	creditCard: 'CC',
	directDebit: 'DIRECT_DEBIT',
	iDeal: 'ONLINE_BANKING',
	payPalBilling: 'DW',
	sofort: 'ONLINE_BANKING',
	wireTransfer: 'OFFLINE_BANKING',
	bancontact: 'CC',
	boleto: 'OFFLINE_BANKING'
};
const checkoutToProviderSubTypeMap = {
	directDebit: 'SEPADIRECTDEBIT',
	iDeal: 'IDEAL',
	payPalBilling: 'PAYPAL',
	sofort: 'SOFO',
	wireTransfer: 'BANKXFER',
	bancontact: 'BANCONTACT_CARD',
	boleto: 'BOLETO'
};

/**
 * Return offline payment method object with required details for making a payment
 * @param {object} options
 * @param {object} options.offlineBankDetails
 * @returns {object}
 */
const getOfflinePaymentObject = ({ offlineBankDetails: paymentDetails }) => {
	return {
		// WireTransfer
		referenceNumber: paymentDetails.paymentReferenceNumber,
		bank: paymentDetails.bank,
		accountHolder: paymentDetails.company,
		location: paymentDetails.city,
		swiftCode: paymentDetails.swiftCode,
		accountNumber: paymentDetails.accountNumber,
		iban: paymentDetails.iban,
		// Boleto
		boletoUrl: paymentDetails.boletoUrl,
		boletoReferenceNumber: paymentDetails.offlineBankResponseNumber
	};
};

/**
 * Transform digitalWalletDetails object returned from HPP payment into eStore API format
 * @param {object} digitalWalletDetails
 * @returns {object}
 */
const walletOwnerToEstoreBilling = (digitalWalletDetails) => ({
	result: {
		billingAddress: {
			firstName: digitalWalletDetails.firstName,
			lastName: digitalWalletDetails.lastName,
			email: digitalWalletDetails.digitalWallet.accountName,
			city: digitalWalletDetails.digitalWallet.address.city,
			country: digitalWalletDetails.digitalWallet.address.countryCode,
			countryName: digitalWalletDetails.digitalWallet.address.provinceState,
			state: digitalWalletDetails.digitalWallet.address.provinceState,
			zipCode: digitalWalletDetails.digitalWallet.address.postalCode,
			company: digitalWalletDetails.digitalWallet.address.company,
			address1: digitalWalletDetails.digitalWallet.address.addressLine1,
			address2: digitalWalletDetails.digitalWallet.address.addressLine2,
			phoneNumber: digitalWalletDetails.digitalWallet.address.phoneNumber
		}
	}
});

/**
 * Schema for credit card payment opition
 * @param {object} data
 * @param {object} data.creditCard NLOK credit card payment option object
 * @returns {object}
 */
export const creditCardSchema = ({ creditCard }) => ({
	brand: creditCard.cardBrand,
	lastFourDigits: String(creditCard.lastFour),
	expirationMonth: Number(creditCard.expirationMonth),
	expirationYear: Number(creditCard.expirationYear)
});

/**
 * Schema for payment opion
 * @param {object} options
 * @param {object} options.paymentOption HPP payment option
 * @param {object} options.paymentOption.type Checkout payment option type (eg. "creditCard"; For guest flow, comes from "createWallet" endpoint)
 * @param {object} options.paymentOption.paymentType HPP payment option type (eg. "CREDIT_CARD"; For authenticated flow, comes from "getPaymentsByGuid" endpoint)
 * @param {object} options.sessionStore Session store instance
 * @returns {object}
 */
export const paymentOptionSchema = ({ paymentOption, sessionStore }) => {
	const paymentDetails = {
		...get(paymentOption, 'creditCardDetails', {}),
		...get(paymentOption, 'digitalWalletDetails', {}),
		...get(paymentOption, 'digitalWalletDetails.digitalWallet', {})
	};
	const providerType = paymentOption.type || paymentOption.paymentType || paymentDetails.paymentSubType || paymentDetails.paymentType;
	const checkoutType = providerToCheckoutTypeMap[providerType] || providerToCheckoutTypeMap[paymentOption.paymentSubType] || providerType;
	const providerSubType = checkoutType === 'creditCard' ? paymentDetails.cardBrand : paymentOption.paymentSubType || checkoutToProviderSubTypeMap[checkoutType];
	const name = checkoutType === providerToCheckoutTypeMap.CC ? providerSubType : checkoutType;
	const isOffline = env.offlineMethods.gen.includes(checkoutType);
	const isManualRenewal = env.manualRenewalMethods.gen.includes(checkoutType);
	const isRedirectable = env.redirectMethods.gen.includes(checkoutType);
	const hasInstallmentsAvailable = env.installmentsMethods.gen.includes(checkoutType);
	const isLibrarySkippable = env.librarySkippableMethods.gen.includes(checkoutType);
	const hasFixedCountry = env.fixedCountryMethods.gen.includes(checkoutType);
	// If type is CC but subType bancontact, return bancontact else original checkoutType
	const paymentOptionType = checkoutType === 'creditCard' && providerSubType === 'BANCONTACT_CARD' ? 'bancontact' : checkoutType;
	const paymentOptionData = {
		id: paymentOption.sourceId,
		type: paymentOptionType,
		sourceId: paymentOption.sourceId,
		providerType: checkoutToProviderTypeMap[checkoutType],
		providerSubType,
		name,
		slug: getSlug(name),
		isOffline,
		isManualRenewal,
		isRedirectable,
		hasInstallmentsAvailable,
		isLibrarySkippable,
		hasFixedCountry
	};
	// Add payment option owner
	if (isObject(paymentDetails.address)) {
		const providerAccount = providerAccountSchema({
			providerAccount: sessionStore.store.providerAccount
		});
		paymentOptionData.owner = customerSchema({
			cart: {
				result: {
					billingAddress: {
						...paymentDetails.address,
						firstName: paymentDetails.firstName,
						lastName: paymentDetails.lastName,
						email: paymentDetails.accountName || providerAccount.email,
						state: paymentDetails.address.provinceState,
						country: paymentDetails.address.countryCode,
						zipCode: paymentDetails.address.postalCode
					}
				}
			}
		});
	}
	// Add payment card data
	if (checkoutType === providerToCheckoutTypeMap.CC) {
		paymentOptionData.card = creditCardSchema({
			creditCard: paymentDetails
		});
	}
	return paymentOptionData;
};

/**
 * Payment schema for payment object
 * @param {object} [data]
 * @param {object} [data.cart] Object with raw cart data from eStore API
 * @param {string[]} [data.customIDs] Array of customIDs from the request
 * @param {object} [data.regionConfig] Object with region configuration
 * @param {object} [data.sessionStore] Session store instance
 * @param {object[]} [data.providerAccountPaymentOptions] Account payment options
 * @returns {object}
 */
export const paymentSchema = ({ cart, customIDs = [], regionConfig, sessionStore, providerAccountPaymentOptions = [] }) => {
	// Load required variables for future use
	let paymentMethods = getProviderPaymentMethods({
		provider: regionConfig.provider,
		customIDs
	});
	// Set paypal as default payment method (1st in array of payment methods)
	if (customIDs.includes('PAYPALFIRST')) {
		paymentMethods = setDefaultPaymentMethod(paymentMethods, 'payPalBilling');
	}
	// Grab correct payment method object (from eStore API response or from session store)
	const payment = sessionStore?.store.paymentMethod;
	// Create base payment object
	let data = {
		bank: {},
		card: {},
		paypal: {},
		redirectUrl: null, // @todo GEN - Missing value
		sessionStatus: null, // @todo GEN - Missing value
		sessionId: null, // @todo GEN - Missing value
		sourceId: isObject(payment) ? payment.sourceId : null,
		sourceClientSecret: isObject(payment) ? payment.sourceClientSecret : null,
		paymentMethods
	};
	// Add provider account payment options
	data.providerAccountPaymentOptions = customIDs.includes('PMSHOW') ? providerAccountPaymentOptions : deduplicatePaymentOptions(providerAccountPaymentOptions);
	// Add HPP payment library token
	data.providerPaymentToken = sessionStore?.store.providerPaymentToken;
	// If there is a selected payment method, just return it's data (authenticated checkout)
	if (sessionStore?.store.selectedPaymentMethod) {
		return {
			...data,
			...sessionStore.store.selectedPaymentMethod
		};
	}
	// Add selected payment data
	if (payment) {
		data = Object.assign({}, data, paymentOptionSchema({
			paymentOption: payment,
			sessionStore
		}));
		// Add owner information
		data.owner = customerSchema({
			cart,
			sessionStore
		});
		// Add digitalWallet details (eg. PayPal)
		if (isObject(payment.digitalWalletDetails)) {
			data.owner = customerSchema({
				cart: walletOwnerToEstoreBilling(payment.digitalWalletDetails),
				sessionStore
			});
		}
		// Add payment card data
		if (payment.type === 'CC' && isObject(payment.creditCardDetails)) {
			data.card = creditCardSchema({
				creditCard: payment.creditCardDetails
			});
		}
		// Add manual renewal payment data
		const paymentDetails = cart.paymentInfo;
		if (data.isManualRenewal && isObject(paymentDetails) && isObject(paymentDetails.offlineBankDetails)) {
			data.offlinePayment = getOfflinePaymentObject(paymentDetails);
		}
	}
	// Return payment data in unified format
	return data;
};

